import { useState, useCallback, useRef, useEffect } from "react";
import { useReducedMotion } from "framer-motion";
import type { MediaShowcaseData } from "../types/narrative";
import { logInfo, logWarning, logError } from "../utils/logger";
import { copyToClipboard } from "../utils/shareCards";
import { useSessionContext } from "../contexts/useSessionContext";

interface UseMediaShowcaseOptions {
  data: MediaShowcaseData;
}

// Template filenames embed `{{narrative_display_name}}`, which can contain
// reserved characters that break downloads or corrupt zip entry names. Strip
// path separators, Windows-reserved characters, and control bytes before any
// filename is handed to `a.download` or `zip.file()`.
function sanitizeFilename(name: string, fallback = "download"): string {
  const cleaned = name
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1f\x7f]/g, "")
    .replace(/[/\\:*?"<>|]/g, "-")
    .replace(/\s+/g, " ")
    .replace(/^[.\s]+|[.\s]+$/g, "")
    .trim();
  return cleaned || fallback;
}

export function useMediaShowcase({ data }: UseMediaShowcaseOptions) {
  const prefersReducedMotion = useReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { recordAction } = useSessionContext();

  const isCarousel = data.items.length > 1;
  const activeItem = data.items[activeIndex];
  const showDownload = data.showDownload !== false;
  const showShare = data.showShare !== false;
  const showDownloadAll = data.showDownloadAll === true && isCarousel;

  // Track active index via IntersectionObserver on scroll-snap children
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !isCarousel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const index = Number((entry.target as HTMLElement).dataset.index);
            if (!isNaN(index)) {
              setActiveIndex(index);
            }
          }
        }
      },
      { root: container, threshold: 0.6 },
    );

    const children = container.querySelectorAll("[data-index]");
    children.forEach((child) => observer.observe(child));

    return () => observer.disconnect();
  }, [isCarousel, data.items.length]);

  const scrollToIndex = useCallback(
    (index: number) => {
      const container = scrollContainerRef.current;
      if (!container) return;
      const child = container.children[index] as HTMLElement | undefined;
      // Under reduced motion, jump instantly instead of the horizontal glide.
      child?.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "nearest",
        inline: "center",
      });
    },
    [prefersReducedMotion],
  );

  const handleDownload = useCallback(
    async (index?: number) => {
      const item = data.items[index ?? activeIndex];
      if (!item) return;

      recordAction("mediaDownload", { filename: item.shareAsset.filename });
      setIsDownloading(true);
      try {
        const response = await fetch(item.shareAsset.src);
        if (!response.ok)
          throw new Error(`Failed to fetch ${item.shareAsset.src}`);
        const blob = await response.blob();
        const filename = sanitizeFilename(
          item.shareAsset.filename ||
            item.shareAsset.src.split("/").pop() ||
            "download",
        );

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        logInfo("Media showcase: downloaded", filename);
      } catch (err) {
        logError("Media showcase: download failed", err);
      } finally {
        setIsDownloading(false);
      }
    },
    [data.items, activeIndex, recordAction],
  );

  const handleShare = useCallback(
    async (index?: number) => {
      const item = data.items[index ?? activeIndex];
      if (!item) return;

      recordAction("mediaShare", { filename: item.shareAsset.filename });
      setIsSharing(true);
      try {
        // If Web Share API is available, use it
        if (navigator.share) {
          const response = await fetch(item.shareAsset.src);
          if (!response.ok)
            throw new Error(`Failed to fetch ${item.shareAsset.src}`);
          const blob = await response.blob();
          const filename = sanitizeFilename(
            item.shareAsset.filename ||
              item.shareAsset.src.split("/").pop() ||
              "share-file",
            "share-file",
          );
          const file = new File([blob], filename, { type: blob.type });

          const text = item.shareText || data.shareText || undefined;
          const title = item.shareTitle || data.shareTitle || undefined;
          const fileShareData: ShareData = { files: [file] };

          if (navigator.canShare?.(fileShareData)) {
            await navigator.share({ ...fileShareData, text, title });
          } else if (text || title) {
            logWarning(
              "Media showcase: file sharing unavailable, sharing text only",
            );
            await navigator.share({ text, title });
          } else {
            logWarning("Media showcase: no supported share payload available");
          }
          logInfo("Media showcase: shared successfully");
        } else {
          // Fallback: download the file (most useful desktop action) if downloads are allowed
          // Then copy share text to clipboard if available
          logInfo(
            "Media showcase: Web Share API unavailable, falling back to download",
          );
          if (showDownload) {
            await handleDownload(index ?? activeIndex);
          }
          const text = item.shareText || data.shareText || "";
          if (text) {
            await copyToClipboard(text);
            logInfo("Media showcase: also copied share text to clipboard");
          }
        }
      } catch (err) {
        // User cancelled share is not an error
        if ((err as DOMException)?.name !== "AbortError") {
          logError("Media showcase: share failed", err);
        }
      } finally {
        setIsSharing(false);
      }
    },
    [data, activeIndex, handleDownload, recordAction, showDownload],
  );

  const handleDownloadAll = useCallback(async () => {
    const zipFilename = sanitizeFilename(
      data.downloadAllFilename || "media-showcase.zip",
      "media-showcase.zip",
    );
    recordAction("mediaDownloadAll", { filename: zipFilename });
    setIsDownloadingAll(true);
    try {
      // Dynamic import JSZip to keep it out of main bundle
      const { default: JSZip } = await import("jszip");
      const zip = new JSZip();

      const fetches = data.items.map(async (item, i) => {
        const response = await fetch(item.shareAsset.src);
        if (!response.ok)
          throw new Error(`Failed to fetch ${item.shareAsset.src}`);
        const blob = await response.blob();
        const filename = sanitizeFilename(
          item.shareAsset.filename ||
            item.shareAsset.src.split("/").pop() ||
            `file-${i}`,
          `file-${i}`,
        );
        return { blob, filename };
      });

      const results = await Promise.allSettled(fetches);
      const usedFilenames = new Map<string, number>();
      const failed: string[] = [];
      for (let i = 0; i < results.length; i++) {
        const result = results[i];
        if (result.status === "fulfilled") {
          const count = usedFilenames.get(result.value.filename) ?? 0;
          usedFilenames.set(result.value.filename, count + 1);
          const filename =
            count === 0
              ? result.value.filename
              : result.value.filename.replace(
                  /(\.[^./]+)?$/,
                  `-${count + 1}$1`,
                );
          zip.file(filename, result.value.blob);
        } else {
          const name = data.items[i].shareAsset.src;
          failed.push(name);
          logError(`Media showcase: failed to fetch ${name}`, result.reason);
        }
      }
      if (failed.length === data.items.length) {
        throw new Error("All downloads failed");
      }
      const zipBlob = await zip.generateAsync({ type: "blob" });

      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = zipFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      logInfo("Media showcase: downloaded all as", zipFilename);
    } catch (err) {
      logError("Media showcase: download all failed", err);
    } finally {
      setIsDownloadingAll(false);
    }
  }, [data, recordAction]);

  return {
    activeIndex,
    activeItem,
    isCarousel,
    scrollContainerRef,
    scrollToIndex,
    showDownload,
    showShare,
    showDownloadAll,
    isDownloading,
    isSharing,
    isDownloadingAll,
    handleDownload,
    handleShare,
    handleDownloadAll,
  };
}
